import { Alert } from '@ksyos/design-system';
import bind from 'bind-decorator';
import { Component } from 'react';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import type { IPatientAddEditValidated } from '../../../../../common/validation/patient';
import type { ValidationErrors } from '../../../../utils/validation';
import { Gender } from '../../../../../common/enums/gender';
import { MaritalStatus } from '../../../../../common/enums/maritalStatus';
import { getObjectKeys } from '../../../../../common/utils/getObjectKeys';
import { UrlBuilder } from '../../../../../common/utils/urlBuilder';
import { patientSsoNotFoundSchema } from '../../../../../common/validation/patient';
import { getJson, postJson } from '../../../../store/fetch';
import { translateKey } from '../../../../translate';
import { emptyValidationErrors, validate } from '../../../../utils/validation';
import { ButtonContainer, CancelButton, ContinueButton, FormContainer, Header, Link } from '../../../elements/common';
import { Field } from '../../../elements/Field';
import { FormRow } from '../../../elements/forms/FormRow';
import { InputContainer } from '../../../elements/forms/InputContainer';
import { DateField } from '../../../fields/Date';
import { RadioGroupField } from '../../../fields/RadioGroup';
import { TextField, TextInput } from '../../../fields/Text';
import { FixedWidthTemplate } from '../../../templates/FixedWidthTemplate';

type PatientNotFoundSsoProps = IWithRouterProps;

interface IPatientNotFoundSsoState {
    errors: ValidationErrors;
    patientData: IPatientData;
}

interface IPatientData {
    birthDate: string;
    emailAddress?: string;
    gender: Gender | null;
    identifierSocialSecurity: string;
    insurancePolicyNumber?: string;
    nameFamily: string;
    nameInfix?: string;
}

class InnerPatientNotFoundSso extends Component<PatientNotFoundSsoProps, IPatientNotFoundSsoState> {
    public state: IPatientNotFoundSsoState = {
        patientData: {
            birthDate: this.props.router.searchParams.get('birthDate') ?? '',
            gender: null,
            identifierSocialSecurity: this.props.router.searchParams.get('identifierSocialSecurity') || '',
            nameFamily: '',
            nameInfix: '',
            insurancePolicyNumber: '',
            emailAddress: '',
        },
        errors: emptyValidationErrors,
    };

    public async componentDidMount() {
        if (this.props.router.searchParams.get('ssoId')) {
            await this.loadSsoData();
        }
    }

    public render() {
        return (
            <FixedWidthTemplate>
                <Header content={translateKey('patientSso.patientNotFound')} />
                <FormContainer
                    formContext={{
                        item: this.state.patientData,
                        placeholderGroupName: 'patientSso',
                        errors: this.state.errors,
                        onChange: this.handleChange,
                        onDoValidate: () => null,
                    }}
                    onSubmit={() => null}
                >
                    <Alert appearance="warning">{translateKey('patientSso.patientNotFoundServiceDown')}</Alert>
                    <Field
                        fieldKey="nameFamily"
                        isRequired={true}
                    >
                        <FormRow>
                            <InputContainer size={'medium'}>
                                <TextInput fieldKey="nameInfix" />
                            </InputContainer>
                            <InputContainer>
                                <TextInput fieldKey="nameFamily" />
                            </InputContainer>
                        </FormRow>
                    </Field>
                    <DateField
                        birthDate={true}
                        fieldKey="birthDate"
                        isRequired={true}
                        label={translateKey('patientSso.birthDateLabel')}
                        size="medium"
                    />
                    <RadioGroupField
                        fieldKey="gender"
                        isRequired={true}
                        labels={this.getGenderLabels()}
                        options={getObjectKeys(Gender)}
                    />
                    <Field
                        fieldKey="identifierSocialSecurity"
                        isRequired={true}
                        label={translateKey('patientSso.identifierSocialSecurity')}
                    >
                        <FormRow>
                            <InputContainer>
                                <TextInput fieldKey="identifierSocialSecurity" />
                            </InputContainer>
                            <InputContainer size={'auto'}>
                                <Link
                                    color="secondary"
                                    to={`/patients/add/sso/${this.props.router.searchParams.get('ssoId')}`}
                                >
                                    {translateKey('patient.noBSN')}
                                </Link>
                            </InputContainer>
                        </FormRow>
                    </Field>
                    <TextField fieldKey="insurancePolicyNumber" />
                </FormContainer>
                <ButtonContainer alignRight>
                    <CancelButton to="/home" />
                    <ContinueButton onClick={this.createNewPatient} />
                </ButtonContainer>
            </FixedWidthTemplate>
        );
    }

    private async loadSsoData() {
        const ssoId = this.props.router.searchParams.get('ssoId');
        const result = await getJson(`/api/thirdPartySsoData/${ssoId}`);
        const data = result.json.data.patient;
        const newPatientDataState = { ...this.state.patientData };
        for (const key of getObjectKeys(this.state.patientData)) {
            if (data[key] !== undefined) {
                newPatientDataState[key] = data[key];
            }
        }

        await this.setState({ patientData: newPatientDataState });
    }

    @bind
    private async createNewPatient() {
        const { errors, hasErrors } = validate(patientSsoNotFoundSchema, this.state.patientData);
        if (hasErrors) {
            await this.setState({ errors: errors });

            return;
        }
        const ssoId = this.props.router.searchParams.get('ssoId');
        const forwardToEpisode = this.props.router.searchParams.get('forwardToEpisode');
        const redirectTo = this.props.router.searchParams.get('redirectTo');
        let url = '/api/patients';
        if (ssoId) {
            url += `?ssoId=${ssoId}`;
        }
        const result = await postJson(url, { patient: this.generatePatientToCreate() });

        if (forwardToEpisode) {
            const urlBuilder = new UrlBuilder('/episode');
            urlBuilder.add('patientId', result.json.id).add('episodeCategory', forwardToEpisode);
            this.props.router.navigate(urlBuilder.build());
        } else if (redirectTo) {
            let redirectUrl = redirectTo;
            redirectUrl += (redirectUrl.indexOf('?') === -1 ? '?' : '&') + `patientId=${result.json.id}`;
            window.location.href = redirectUrl;
        } else {
            this.props.router.navigate(`/patients/${result.json.id}`);
        }
    }

    private generatePatientToCreate() {
        const patient: IPatientAddEditValidated = {
            birthDate: this.state.patientData.birthDate,
            gender: this.state.patientData.gender!,
            identifierSocialSecurity: this.state.patientData.identifierSocialSecurity,
            nameFamily: this.state.patientData.nameFamily,
            nameInfix: this.state.patientData.nameInfix || null,
            careProviderOrganizationId: null,
            careProviderPractitionerId: null,
            city: null,
            country: null,
            deceasedDate: null,
            emailAddress: this.state.patientData.emailAddress || null,
            homePhoneNumber: null,
            houseLetter: null,
            houseNumber: null,
            houseNumberSuffix: null,
            isDeceased: false,
            maritalStatus: MaritalStatus.unknown,
            mobileNumber: null,
            nameFamily2: null,
            nameGiven: null,
            nameInfix2: null,
            nameUsual: null,
            profession: null,
            state: null,
            street: null,
            zip: null,
        };

        return patient;
    }

    @bind
    private async handleChange<K extends keyof IPatientData>(key: K, value: IPatientData[K]) {
        await this.setState({ patientData: { ...this.state.patientData, [key]: value } });
    }
    private getGenderLabels() {
        return getObjectKeys(Gender).map((gender) => translateKey(`genderEnum.${gender}`));
    }
}

export const PatientNotFoundSso = withRouter(InnerPatientNotFoundSso);
